// studioLayout — where each card sits on the Studio canvas, and how the view
// zooms. Pure.
//
// Positions start from a deterministic auto-layout (sources in the left
// column, materials and modules to the right, modules wider), and a card the
// author has moved keeps where they put it. The moved positions are a plain
// `id → point` map: per device in v0 (app/studioLayoutPreference.ts), shared
// project state once projects exist (#92).

export type {
    Direction,
    Placement,
    Point,
    Size,
    StoredView,
    StudioLayout,
    StudioView,
};
export {
    ARROW_STEP,
    ARROW_STEP_LARGE,
    clampZoom,
    emptyLayout,
    freeSpotNear,
    initialView,
    keepCentre,
    newCardSize,
    nudge,
    pinned,
    placeCards,
    restoreView,
    storedViewOf,
    wheelZoom,
    withPosition,
    zoomAround,
    zoomIn,
    zoomOut,
};

import type { StudioCard } from "./studioCards.js";

type Point = { readonly x: number; readonly y: number };
type CardSize = { readonly width: number; readonly height: number };
type Placement = Point & CardSize;

// The moved cards, by id. A card without an entry sits where auto-layout puts
// it.
type StudioLayout = { readonly positions: ReadonlyMap<string, Point> };

const emptyLayout: StudioLayout = { positions: new Map() };

// Canvas units (1 unit = 1 CSS pixel at 100 %). The card sizes are the
// design's (pen "Kort" 260 wide, "Modul" 360 wide, a part and its seam 58);
// the heights are estimates (what is drawn is measured where it matters).
const CARD_WIDTH = 260;
const CARD_HEIGHT = 216;
const MODULE_WIDTH = 360;
const MODULE_HEAD = 100;
const MODULE_PART = 58;
const GAP = 32;
const ORIGIN: Point = { x: 48, y: 96 };

// How far one arrow-key press moves a focused card; with Shift, further.
const ARROW_STEP = 8;
const ARROW_STEP_LARGE = 40;

// A card's footprint. A container grows with its parts (a course's modules
// folded); every other card is one size (two title lines, one status line).
const cardSize = (card: StudioCard): CardSize =>
    card.type === "container"
        ? {
              width: MODULE_WIDTH,
              height: MODULE_HEAD + card.parts.length * MODULE_PART,
          }
        : { width: CARD_WIDTH, height: CARD_HEIGHT };

const isLeftColumn = (card: StudioCard): boolean =>
    card.type === "source" || card.type === "generic";

// Stack `cards` top-down in a column starting at `x`.
const stack = (
    cards: readonly StudioCard[],
    x: number,
): readonly (readonly [string, Point])[] =>
    cards.reduce<{
        readonly y: number;
        readonly out: readonly (readonly [string, Point])[];
    }>(
        (acc, card) => ({
            y: acc.y + cardSize(card).height + GAP,
            out: [...acc.out, [card.id, { x, y: acc.y }]],
        }),
        { y: ORIGIN.y, out: [] },
    ).out;

// The auto-layout: inputs (sources, other files) in the left column, what was
// made from them to the right. Deterministic for the same cards in the same
// order.
const autoLayout = (cards: readonly StudioCard[]): ReadonlyMap<string, Point> =>
    new Map([
        ...stack(cards.filter(isLeftColumn), ORIGIN.x),
        ...stack(
            cards.filter((c) => !isLeftColumn(c)),
            ORIGIN.x + CARD_WIDTH + GAP * 3,
        ),
    ]);

// Every card's placement: where the author put it, else the auto-layout's.
const placeCards = (
    cards: readonly StudioCard[],
    layout: StudioLayout,
): ReadonlyMap<string, Placement> => {
    const auto = autoLayout(cards);
    return new Map(
        cards.map((card) => {
            const at = layout.positions.get(card.id) ??
                auto.get(card.id) ?? { x: ORIGIN.x, y: ORIGIN.y };
            return [card.id, { ...at, ...cardSize(card) }];
        }),
    );
};

// The layout with one card moved (rounded to whole units).
const withPosition = (
    layout: StudioLayout,
    id: string,
    at: Point,
): StudioLayout => ({
    positions: new Map([
        ...layout.positions,
        [id, { x: Math.round(at.x), y: Math.round(at.y) }],
    ]),
});

// === Placing a new card =======================================================
// A new card (a container made from cards, a toolbar material) goes in the
// free spot nearest to where it was asked for, so new cards never stack on
// top of each other or of what is there (D10).

// The size a new card will have: a container of `parts` parts, or a leaf.
const newCardSize = (parts: number | null): CardSize =>
    parts === null
        ? { width: CARD_WIDTH, height: CARD_HEIGHT }
        : { width: MODULE_WIDTH, height: MODULE_HEAD + parts * MODULE_PART };

// How far apart the tried spots are, and how many rings out it looks.
const SPOT_STEP = GAP * 2;
const SPOT_RINGS = 24;

const overlaps = (a: Placement, b: Placement): boolean =>
    a.x < b.x + b.width + GAP &&
    b.x < a.x + a.width + GAP &&
    a.y < b.y + b.height + GAP &&
    b.y < a.y + a.height + GAP;

// The grid steps `r` rings out from the centre, nearest first.
const ring = (r: number): readonly Point[] => {
    const side = Array.from({ length: 2 * r + 1 }, (_, i) => i - r);
    return side
        .flatMap((x) =>
            side
                .filter((y) => Math.max(Math.abs(x), Math.abs(y)) === r)
                .map((y) => ({ x, y })),
        )
        .sort((a, b) => Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y));
};

// The free spot nearest `at` (its top-left) for a card of `size`, clear of
// every placement but `ignore` (the cards about to go inside it); `at` when
// nothing nearby is free.
const freeSpotNear = (
    at: Point,
    size: CardSize,
    placements: ReadonlyMap<string, Placement>,
    ignore: ReadonlySet<string> = new Set(),
): Point => {
    const others = [...placements]
        .filter(([id]) => !ignore.has(id))
        .map(([, p]) => p);
    const spots = Array.from({ length: SPOT_RINGS + 1 }, (_, r) =>
        ring(r),
    ).flatMap((steps) =>
        steps.map((s) => ({
            x: at.x + s.x * SPOT_STEP,
            y: at.y + s.y * SPOT_STEP,
        })),
    );
    return (
        spots.find((p) =>
            others.every((o) => !overlaps({ ...p, ...size }, o)),
        ) ?? at
    );
};

// Every card kept where it is now. Before cards go into a container (and
// leave the canvas), so the auto-placed ones do not close the gap and slide
// under a card just placed there.
const pinned = (
    layout: StudioLayout,
    placements: ReadonlyMap<string, Placement>,
): StudioLayout => ({
    positions: new Map([
        ...[...placements].map(([id, p]): [string, Point] => [
            id,
            { x: p.x, y: p.y },
        ]),
        ...layout.positions,
    ]),
});

type Direction = "up" | "down" | "left" | "right";

// A point moved `step` units.
const nudge = (at: Point, direction: Direction, step: number): Point => {
    switch (direction) {
        case "up":
            return { x: at.x, y: at.y - step };
        case "down":
            return { x: at.x, y: at.y + step };
        case "left":
            return { x: at.x - step, y: at.y };
        case "right":
            return { x: at.x + step, y: at.y };
    }
};

// Zoom steps, as fractions (25 % … 200 %).
const ZOOM_STEPS: readonly number[] = [
    0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2,
];
const ZOOM_DEFAULT = 1;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 2;

const clampZoom = (zoom: number): number =>
    Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom));

const zoomIn = (zoom: number): number =>
    ZOOM_STEPS.find((s) => s > zoom + 0.001) ?? ZOOM_MAX;

const zoomOut = (zoom: number): number =>
    [...ZOOM_STEPS].reverse().find((s) => s < zoom - 0.001) ?? ZOOM_MIN;

// What the viewport shows: the zoom, and where the canvas origin sits in the
// viewport (CSS pixels). View state, per person; never stored.
type StudioView = { readonly zoom: number; readonly pan: Point };

const initialView: StudioView = { zoom: ZOOM_DEFAULT, pan: { x: 0, y: 0 } };

// Zoom to `next` (clamped) about `anchor`, a viewport point: the canvas point
// under the anchor stays under it (the pointer for a pinch or ctrl+wheel, the
// viewport's centre for the − / + buttons).
const zoomAround = (
    view: StudioView,
    anchor: Point,
    next: number,
): StudioView => {
    const zoom = clampZoom(next);
    const k = zoom / view.zoom;
    return {
        zoom,
        pan: {
            x: anchor.x - (anchor.x - view.pan.x) * k,
            y: anchor.y - (anchor.y - view.pan.y) * k,
        },
    };
};

// The zoom one ctrl+wheel (or trackpad pinch) event asks for. A pinch sends
// many small deltas, a mouse wheel few large ones; capping the delta keeps a
// mouse notch to about one zoom step while a pinch stays smooth.
const WHEEL_CAP = 40;
const WHEEL_RATE = 0.005;
const wheelZoom = (zoom: number, deltaY: number): number =>
    clampZoom(
        zoom *
            Math.exp(
                -Math.max(-WHEEL_CAP, Math.min(WHEEL_CAP, deltaY)) * WHEEL_RATE,
            ),
    );

// === Remembering the view =====================================================
// What the Studio showed last, kept per device (per person, like zoom and
// scroll in the spec): the zoom and the canvas point at the viewport's
// centre, so a different window size still shows the same place.

type Size = { readonly width: number; readonly height: number };
type StoredView = { readonly zoom: number; readonly centre: Point };

const storedViewOf = (view: StudioView, size: Size): StoredView => ({
    zoom: view.zoom,
    centre: {
        x: (size.width / 2 - view.pan.x) / view.zoom,
        y: (size.height / 2 - view.pan.y) / view.zoom,
    },
});

const viewAt = (stored: StoredView, size: Size): StudioView => {
    const zoom = clampZoom(stored.zoom);
    return {
        zoom,
        pan: {
            x: size.width / 2 - stored.centre.x * zoom,
            y: size.height / 2 - stored.centre.y * zoom,
        },
    };
};

// Whether any card is (partly) in sight.
const showsAnyCard = (
    view: StudioView,
    size: Size,
    placements: ReadonlyMap<string, Placement>,
): boolean => {
    const left = -view.pan.x / view.zoom;
    const top = -view.pan.y / view.zoom;
    const right = left + size.width / view.zoom;
    const bottom = top + size.height / view.zoom;
    return [...placements.values()].some(
        (p) =>
            p.x < right &&
            p.x + p.width > left &&
            p.y < bottom &&
            p.y + p.height > top,
    );
};

// Every card in sight, at most 100 %, centred; no cards → the default view.
// Clear of the floating crumb and the zoom/tools bars.
const FIT_MARGIN = 80;
const fitView = (
    placements: ReadonlyMap<string, Placement>,
    size: Size,
): StudioView => {
    const boxes = [...placements.values()];
    if (boxes.length === 0) return initialView;
    const minX = Math.min(...boxes.map((b) => b.x));
    const minY = Math.min(...boxes.map((b) => b.y));
    const maxX = Math.max(...boxes.map((b) => b.x + b.width));
    const maxY = Math.max(...boxes.map((b) => b.y + b.height));
    const zoom = clampZoom(
        Math.min(
            1,
            (size.width - 2 * FIT_MARGIN) / (maxX - minX),
            (size.height - 2 * FIT_MARGIN) / (maxY - minY),
        ),
    );
    return viewAt(
        { zoom, centre: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 } },
        size,
    );
};

// The view to open on: the remembered one while it still shows a card (cards
// can have moved or gone since), else every card fitted in.
const restoreView = (
    stored: StoredView | null,
    placements: ReadonlyMap<string, Placement>,
    size: Size,
): StudioView => {
    if (stored !== null) {
        const view = viewAt(stored, size);
        if (showsAnyCard(view, size, placements)) return view;
    }
    return fitView(placements, size);
};

// A resized viewport keeps what was at its centre at its centre.
const keepCentre = (view: StudioView, from: Size, to: Size): StudioView => ({
    zoom: view.zoom,
    pan: {
        x: view.pan.x + (to.width - from.width) / 2,
        y: view.pan.y + (to.height - from.height) / 2,
    },
});
